import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { StampBillableChargeTableRow } from '../../../../../core/models/parking/stamp-discount.model';
import { ApiStampDiscountService } from '../../../../../core/services/api/parking/api-stamp-discount.service';
import { MapperStampDiscountService } from '../../../../../core/mappers/parking/mapper-stamp-discount.service';
import { MapperParkingMasterDataService } from '../../../../../core/mappers/system-settings/mapper-parking-master-data.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { VatSettingService } from '../../../../../core/services/vat-setting.service';
import { extractVatFromInclusive } from '../../../../../core/utils/vat-amount.util';
import { PssConfirmModalComponent } from '../../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { ModalAdminActivityHistoryComponent } from '../../system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

type BillableStatus = 'pending' | 'invoiced' | 'cancelled';

@Component({
  selector: 'app-modal-stamp-billable-charge-edit',
  standalone: true,
  imports: [CommonModule, FormsModule, PssConfirmModalComponent, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-stamp-billable-charge-edit.component.html',
  styleUrl: './modal-stamp-billable-charge-edit.component.css',
})
export class ModalStampBillableChargeEditComponent implements OnInit {
  @Input({ required: true }) row!: StampBillableChargeTableRow;
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;
  readonly statusOptions: { value: BillableStatus; label: string }[] = [
    { value: 'pending', label: 'รอดำเนินการ' },
    { value: 'invoiced', label: 'ออกใบแจ้งหนี้แล้ว' },
    { value: 'cancelled', label: 'ยกเลิก' },
  ];

  /** ยอดรวม VAT แล้ว (ตรงกับ companyFee / Total ใน DB) */
  editTotal = 0;
  editStatus: BillableStatus = 'pending';
  isSaving = signal(false);
  isFetchingFee = signal(false);
  showConfirm = signal(false);
  lastFetchedCompanyFee = signal<number | null>(null);

  private readonly api = inject(ApiStampDiscountService);
  private readonly mapper = inject(MapperStampDiscountService);
  private readonly actionMapper = inject(MapperParkingMasterDataService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  readonly vatSetting = inject(VatSettingService);

  /** ยอดก่อน VAT แยกจากยอดรวม (สูตรเดียวกับ API StampBillableChargeCommandService) */
  get previewFeeExVat(): number {
    return extractVatFromInclusive(this.editTotal, this.vatSetting.ratePercent()).subTotal;
  }

  get previewVat(): number {
    return extractVatFromInclusive(this.editTotal, this.vatSetting.ratePercent()).vatAmount;
  }

  ngOnInit(): void {
    // ช่องแก้ไข = รวม VAT (Total); fallback ถ้ายังไม่มี total
    const total = Number(this.row.total ?? 0);
    this.editTotal = total > 0 ? total : Number(this.row.fee ?? 0);
    const st = (this.row.status || 'pending').toLowerCase();
    this.editStatus = (st === 'invoiced' || st === 'cancelled' ? st : 'pending') as BillableStatus;
  }

  onClose(): void {
    if (this.isSaving() || this.isFetchingFee()) return;
    this.close.emit();
  }

  /** ดึง companyFee จากคำนวณ local แล้วใส่ช่องยอดค่าธรรมเนียม */
  fetchCompanyFee(): void {
    if (this.isSaving() || this.isFetchingFee()) return;

    const transactionId = Number(this.row.parkingTransactionId ?? 0);
    const siteId = Number(this.row.siteId ?? 0);
    if (!transactionId || !siteId) {
      this.toast.error('ไม่พบ transaction หรือ site สำหรับคำนวณ');
      return;
    }

    this.isFetchingFee.set(true);
    this.api.calculateLocalParkingFee(transactionId, siteId).subscribe({
      next: (res) => {
        this.isFetchingFee.set(false);
        const mapped = this.mapper.mapLocalParkingFeeCalculation(res);
        if (!mapped.success) {
          this.toast.error(mapped.message || 'คำนวณราคาไม่สำเร็จ');
          return;
        }
        const total = Math.round(mapped.companyFee * 100) / 100;
        this.editTotal = total;
        this.lastFetchedCompanyFee.set(total);
        this.toast.success(`ดึงราคาบริษัทแล้ว (รวม VAT): ${total.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      },
      error: (err) => {
        this.isFetchingFee.set(false);
        const body = err?.error;
        const mapped = body ? this.mapper.mapLocalParkingFeeCalculation(body) : null;
        this.toast.error(mapped?.message || err?.error?.message || 'ดึงราคาไม่สำเร็จ');
      },
    });
  }

  durationText(minutes: number | null): string {
    return this.mapper.formatParkingDuration(minutes);
  }

  statusLabel(status: string): string {
    return this.mapper.statusLabelTh(status);
  }

  requestSave(): void {
    if (this.isFetchingFee()) return;
    if (!this.permissionService.canCreate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์สร้าง/แก้ไขรายการ');
      return;
    }
    if (this.editTotal < 0 || Number.isNaN(this.editTotal)) {
      this.toast.error('ยอดค่าธรรมเนียมไม่ถูกต้อง');
      return;
    }
    this.showConfirm.set(true);
  }

  cancelConfirm(): void {
    if (this.isSaving() || this.isFetchingFee()) return;
    this.showConfirm.set(false);
  }

  confirmSave(): void {
    const userId = Number(this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(this.router.url) || 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลสิทธิ์ผู้ใช้งาน');
      return;
    }

    this.isSaving.set(true);
    this.api.updateStampBillableCharge({
      userId,
      menuId,
      id: this.row.id,
      // ส่งยอดรวม VAT — API แยก Fee / Vat / Total
      fee: this.editTotal,
      status: this.editStatus,
    }).subscribe({
      next: (res) => {
        this.isSaving.set(false);
        const mapped = this.actionMapper.mapActionResponse(res);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'บันทึกสำเร็จ');
          this.showConfirm.set(false);
          this.saveSuccess.emit();
          this.close.emit();
        } else {
          this.toast.error(mapped.message || 'บันทึกไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        this.toast.error(err?.error?.message || 'บันทึกไม่สำเร็จ');
      },
    });
  }

  get canShowActivityHistory(): boolean {
    return !!this.row?.id && this.row.id > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
